import { useCallback, useEffect, useState } from 'react'

import { EventServices } from '@/middlewares/services'

/**
 * The shell's event summary (`GET /event/menu/:eventIdentifier`): identity, menu gates, publish
 * status, roles. Cached per identifier for the session so crossing the public / private route blocks
 * (which remount the shell) neither refetches nor flashes the rail; `invalidate()` after a mutation.
 */
const cache = new Map()

export const invalidateOrgEvent = (eventIdentifier) => {
    if (eventIdentifier) cache.delete(String(eventIdentifier))
    else cache.clear()
}

export default function useOrgEvent(eventIdentifier, { enabled = true } = {}) {
    const key = String(eventIdentifier || '')
    const [state, setState] = useState(() =>
        cache.has(key) ? { event: cache.get(key), loading: false } : { event: null, loading: enabled },
    )

    const load = useCallback(async () => {
        if (!key) return
        try {
            const response = await EventServices.getEventMenu(key)
            cache.set(key, response.data)
            setState({ event: response.data, loading: false })
        } catch (error) {
            // 401 (logged out meanwhile) / 404: no shell, the page handles its own errors
            if (error?.response?.status !== 401 && error?.response?.status !== 404) {
                console.error('/event/menu - error', error)
            }
            cache.set(key, null)
            setState({ event: null, loading: false })
        }
    }, [key])

    useEffect(() => {
        if (!enabled) {
            setState({ event: null, loading: false })
            return
        }
        if (cache.has(key)) {
            setState({ event: cache.get(key), loading: false })
            return
        }
        setState({ event: null, loading: true })
        load()
    }, [key, enabled, load])

    /** Local patch (identity edit, toggle) mirrored into the cache. */
    const patch = useCallback(
        (update) => {
            setState((previous) => {
                if (!previous.event) return previous
                const next = typeof update === 'function' ? update(previous.event) : { ...previous.event, ...update }
                cache.set(key, next)
                return { event: next, loading: false }
            })
        },
        [key],
    )

    return { event: state.event, loading: state.loading, reload: load, patch }
}
